<template>
  <div style="padding-left: 10px">
    <vue-headful :title="$t('TITLE_TYPE_NAME', { type: $t('PREFERENCES') }) | formatTitle" />
    <main-section
      v-if="currentUser"
      :typename="'user'"
      :item="user"
      :isEditMode="true"
      :itemType="'PREFERENCES'"
      :header="$t('PREFERENCES')"
      :onUpdate="updateUser"
      :isDiff="diffActive"
      @change-chosen="onChangeChosen"
      @revert="onRevert"
      ref="mainSection"
      class="userPreferences"
    >
      <fieldset slot="fieldset" :disabled="isOutdated">
        <div class="ui grid">
          <div class="sixteen wide column" v-if="!userExists">
            <div class="ui message">
              <div class="header">
                {{ $t('NO_USER_HEADER') }}
              </div>
              <p>{{ $t('NO_USER_BODY', { withName: currentUser }) }}</p>
              <router-link :to="{ name: 'new-user' }">
                {{ $t('CREATE', { thing: $tc('USER', 1) }) }}
              </router-link>
            </div>
          </div>
          <template v-else>
            <div class="twelve wide column">
              <div class="ui basic segment" v-for="category in categories" :key="category.name">
                <h4
                  class="ui horizontal left aligned divider header"
                  :id="'id_' + category.name"
                  ref="categories"
                >
                  {{ $t(category.name) }}
                </h4>
                <div class="ui basic segment">
                  <template v-for="setting in category.settings">
                    <div
                      class="field"
                      :key="setting.name"
                      :class="{ disabled: diffActive }"
                      v-if="setting.type === types.BOOL"
                      v-diff-field.disable="userDiff.preferences[setting.name]"
                    >
                      <div class="ui toggle checkbox">
                        <input :id="'id_' + setting.name" type="checkbox" v-model="setting.value" />
                        <label :for="'id_' + setting.name">{{ $t(setting.description) }}</label>
                      </div>
                    </div>
                    <typeahead
                      :key="setting.name"
                      v-else-if="setting.type === types.ENUM"
                      :items="
                        setting
                          .enumValues($locale)
                          .map(v => ({ name: setting.toDisplayValue(v, $i18n), id: v + '' }))
                      "
                      :label-text="$t(setting.description)"
                      :current-item="setting.toDisplayValue(setting.value, $i18n)"
                      :select-handler="x => (setting.value = x.id)"
                      :diff="userDiff.preferences[setting.name]"
                    />
                    <div v-else-if="setting.component" :key="setting.name">
                      <component
                        :is="setting.component"
                        v-bind="{ value: setting.value, thisUser: user }"
                        @update="onCustomSettingUpdate(setting, $event)"
                      />
                    </div>
                    <div v-else :key="setting.name">
                      TODO: No templating for {{ setting.type }} preferences yet
                    </div>
                  </template>
                </div>
              </div>
              <div class="ui basic segment">
                <h4 class="ui horizontal left aligned divider header" ref="clientSettings">
                  {{ $t('CACHE_HEADER') }}
                </h4>
                <div class="ui basic segment">
                  <div class="field">
                    <div class="ui primary labeled icon button" @click="flushDbCache">
                      {{ $t('CACHE_FLUSH_BUTTON_TEXT') }}
                      <i class="recycle icon" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="four wide column">
              <div class="ui sticky fixed">
                <h3 class="header">{{ $t('PREFERENCES_CATEGORIES') }}</h3>
                <div class="ui vertical following fluid accordion text menu">
                  <a
                    class="item"
                    v-for="(category, idx) in categories"
                    @click="$refs.categories[idx].scrollIntoView({ behavior: 'smooth' })"
                    :key="category.name"
                    >{{ $t(category.name) }}</a
                  >
                  <a
                    class="item"
                    @click="$refs.clientSettings.scrollIntoView({ behavior: 'smooth' })"
                    >{{ $t('CACHE_HEADER') }}</a
                  >
                </div>
              </div>
            </div>
          </template>
        </div>
      </fieldset>
      <loading-confirm-button
        slot="left-bottom-menu"
        class="ui right icon red button"
        :onClick="onReset"
        :title="$t('RESET_TO_DEFAULTS')"
        :confirmMessage="$t('RESET_TO_DEFAULTS_CONFIRM_MESSAGE', { thing: $t('PREFERENCES') })"
        :disabled="userIsAtDefaults || isOutdated || user.isDiff"
      >
        <i class="radiation icon"></i>
      </loading-confirm-button>
    </main-section>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

.userPreferences {
  .ui.form .field {
    // A bit more vertical spacing since this is a veritcal listing of input controls rather than a typical form
    margin-bottom: 2em;
  }
  .ui.form .field:last-child {
    margin-bottom: 0;
  }
}
</style>

<script>
// @flow
import Vue from 'vue'
import { Auth, User, Toast, ErrorCodes, UnsavedChecker, IndexedDb } from 'services'
import {
  getPreferenceDeclarations,
  getDefaultPreferenceObject,
  preferenceCategories,
  preferenceTypes
} from '../../services/User'
import { MainSection, LoadingConfirmButton, Typeahead } from 'ui'

const preferenceDeclarations = getPreferenceDeclarations()
const defaultPreferenceObject = getDefaultPreferenceObject()

export default {
  name: 'userpreferences',
  mixins: [UnsavedChecker.mixin],
  components: { MainSection, LoadingConfirmButton, Typeahead },

  data() {
    return {
      user: {},
      userDiff: { preferences: {} },
      isOutdated: false,
      categories: []
    }
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser || Auth.getLoggedInUser()
    },
    types() {
      return preferenceTypes
    },
    userIsAtDefaults() {
      return _.isEmpty(this.user.preferences)
    },
    diffActive() {
      return this.user.isDiff
    },
    userExists() {
      return !_.isEmpty(this.user)
    },
    features() {
      return this.$store.state.config.features
    }
  },

  methods: {
    async flushDbCache() {
      const db = await IndexedDb.getExisting()
      if (db) await db.clearAllStores()
    },
    onCustomSettingUpdate(setting, value) {
      Vue.set(setting, 'value', value)
    },
    onReset() {
      this.$set(this.user, 'preferences', {})
      this.updateCategories(this.user.preferences)
      this.$refs.mainSection.onUpdateAction()
    },
    updateCategories(userPreferences) {
      const buckets = _.groupBy(preferenceDeclarations, decl => decl.category)
      const categories = _.uniq(_.map(preferenceDeclarations, decl => decl.category))
      this.categories = categories
        .map(category => {
          const bucket = buckets[category]
          return {
            name: category,
            settings: bucket
              .filter(
                setting =>
                  !setting.dependsOnFeature || _.includes(this.features, setting.dependsOnFeature)
              )
              .map(setting => {
                const value = _.get(userPreferences, setting.name)
                return _.extend(setting, { value: value == null ? setting.default : value })
              })
          }
        })
        .filter(c => !_.isEmpty(c?.settings))
    },
    toPersistableObject(categories, currentUserPrefs) {
      // Return flat object like { key1: val1, key2: val2, key3: val3 }
      const result = {}
      for (const category of categories) {
        for (const setting of category.settings) {
          // Don't persist the setting if it is pristine (the user never touched it)
          // It is pristine if the user.preferences doesn't mention it and the ui version is the default value
          if (!(currentUserPrefs[setting.name] == null && setting.value === setting.default))
            result[setting.name] = setting.value
        }
      }
      return result
    },
    fetchUser(userName: string) {
      return User.byName(userName)
        .then(users => {
          if (users == null || users.length === 0) throw Error('No users found')
          if (users.length > 1) throw Error('Too many users found')
          this.user = _.cloneDeep(users[0])
          this.updateCategories(this.user.preferences)
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('PREFERENCES', 1) })
            })
          )
          console.error(err)
        })
    },
    updateUser() {
      if (!this.isOutdated) {
        return User.updateUser(this.user.id, this.user)
          .then(res => {
            this.user.version = res.version
            this.$locale.change(this.user.preferences.language || this.$locale.current() || 'en')
            Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('PREFERENCES', 1) }), {
              remove: 5000
            })
          })
          .catch(err => {
            if (err.code === ErrorCodes.ITEM_OUTDATED) {
              this.isOutdated = true
              Toast.error(
                this.$i18n.t('ERR_OUTDATED_VERSION', {
                  action: this.$i18n.t('UPDATE', {
                    thing: this.$i18n.tc('PREFERENCES', 1)
                  }),
                  thing: this.$i18n.tc('PREFERENCES', 1)
                })
              )
            } else {
              Toast.error(
                this.$i18n.t('ERR', {
                  action: this.$i18n.t('UPDATE', {
                    thing: this.$i18n.tc('PREFERENCES', 1)
                  })
                }),
                { remove: 5000 }
              )
              console.error(err)
            }
            throw err
          })
      } else {
        Toast.error(
          this.$i18n.t('ERR_OUTDATED_VERSION', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('PREFERENCES', 1) }),
            thing: this.$i18n.tc('PREFERENCES', 1)
          })
        )
        return Promise.reject('Outdated data detected')
      }
    },
    onChangeChosen({ changedItem, fullDiff }) {
      this.user = changedItem
      this.userDiff = !_.isEmpty(fullDiff) ? fullDiff : { preferences: {} }
      this.updateCategories(changedItem.preferences)
    },
    onRevert(user) {
      this.user = user
      this.userDiff = { preferences: {} }
      // NOTE: We revert only the preferences part of the user object and leave the rest as is
      this.user.preferences = user.preferences
      this.updateCategories(this.user.preferences)
    }
  },

  mounted() {
    User.readAllUsers()
    if (this.currentUser) {
      this.fetchUser(this.currentUser)
    }
  },

  watch: {
    categories: {
      deep: true,
      handler(newCategories) {
        if (!this.user.isDiff)
          this.$set(
            this.user,
            'preferences',
            this.toPersistableObject(newCategories, this.user.preferences || {})
          )
      }
    }
  }
}
</script>
